import React, { useState, useEffect } from "react";
import styles from "./ChatTestPage.module.scss";
import axiosInstance from "../../api/axios";
import { useTranslation } from "react-i18next";
import { ROUTES } from "../../constants/routes";
import { useDispatch } from "react-redux";
import useLikeClick from "../../hooks/useLikeClick";
import useFavouriteClick from "../../hooks/useFavouriteClick";
import { setVoteCount } from "../../store/slices/lists.slice";
import { setLoginModal } from "../../store/slices/modals.slice";
import { setShareModal } from "../../store/slices/modals.slice";
import { useAppSelector } from "../../store/store";
import Footer from "../../components/footer/Footer";
import Header from "../../components/header/Header";
import MovieCarouselDesktop from "./components/MovieCarouselDesktop"
import MovieCarouselMobile from "./components/MovieCarouselMobile"
import useInterfaceContext from "../../components/providers/interface";
import LoginModal from "../../components/shared/LoginModal/LoginModal";
import ShareModal from "../../components/shared/ShareModal/ShareModal";
import ArrowUp from "../../components/icons/chat/arrowUp";
import AiIcon from "../../components/icons/chat/aiIcon";
import GradientBackdrop from "../../components/shared/GradientBackdrop/GradientBackdrop";

interface Movie {
  id: number;
  title: string;
  title_ru: string;
  title_ua: string;
  title_ar: string;
  poster_path: string;
  backdrop_path: string;
  trailer_key?: string | null;
  release_date: string;
  vote_average: number;
  vote_count: number;
  imdb_rating?: number | null;
  rotten_tomatoes_rating?: number | null;
  genre_ids: number[];
  overview: string;
  overview_ru: string;
  overview_ua: string;
  liked: boolean;
  in_favorite: boolean;
  shares_count: number;
  favorites_count: number;
}

interface ChatMessage {
  id: number;
  role: "user" | "assistant";
  content: string;
  movie_ids: number[];
  movies: Movie[];
  created_at: string;
}

// A new UUID is generated every time the JS module is evaluated (i.e., on every true page load/refresh).
// It's stored alongside the cached data in sessionStorage so we can tell whether the data
// belongs to the current page load (keep it) or a previous one (discard it).
const _PAGE_LOAD_ID = crypto.randomUUID();
const _CACHE_KEY = "chatCache";

interface ChatCache {
  loadId: string;
  messages: ChatMessage[];
  conversationId: number | null;
}

function readCache(): ChatCache | null {
  try {
    const raw = sessionStorage.getItem(_CACHE_KEY);
    if (!raw) return null;
    const cache: ChatCache = JSON.parse(raw);
    return cache.loadId === _PAGE_LOAD_ID ? cache : null;
  } catch {
    return null;
  }
}

function writeCache(messages: ChatMessage[], conversationId: number | null) {
  try {
    const cache: ChatCache = { loadId: _PAGE_LOAD_ID, messages, conversationId };
    sessionStorage.setItem(_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // ignore quota errors
  }
}

const ChatTestPage: React.FC = () => {
  const { isMobile } = useInterfaceContext();
  const { i18n, t } = useTranslation();
  const dispatch = useDispatch();

  const { shareModal, loginModal } = useAppSelector(
    (state: any) => state.modals
  );

  const [shareMovieId, setShareMovieId] = useState<number | null>(null);

  const openLoginModal = () => {
    dispatch(setLoginModal(true));
  };

  const { likesList, likeHandler } = useLikeClick();
  const { favoritesList, favouritesHandler } = useFavouriteClick();

  const updateVoteCount = (id: any, key: any, type: any) => {
    dispatch(setVoteCount({ id, key, type }));
  };

  const [messages, setMessages] = useState<ChatMessage[]>(() => readCache()?.messages ?? []);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState<number | null>(() => readCache()?.conversationId ?? null);
  const [sessionKey] = useState(
    () => localStorage.getItem("chatSessionKey") || crypto.randomUUID()
  );
  useEffect(() => {
    localStorage.setItem("chatSessionKey", sessionKey);
  }, [sessionKey]);

  useEffect(() => {
    writeCache(messages, conversationId);
  }, [messages, conversationId]);

  const langMap: Record<string, string> = {
    en: "en-US",
    ru: "ru-RU",
    uk: "uk-UA",
    ar: "ar-SA",
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userText = input.trim();
    setInput("");

    const userMsg: ChatMessage = {
      id: Date.now(),
      role: "user",
      content: userText,
      movie_ids: [],
      movies: [],
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setLoading(true);

    try {
      const payload: any = {
        message: userText,
        language: langMap[i18n.language] || "en-US",
      };
      if (conversationId) {
        payload.conversation_id = conversationId;
      }

      const { data } = await axiosInstance.post("chat/", payload, {
        headers: { "X-Chat-Session": sessionKey },
      });

      setConversationId(data.conversation_id);

      const assistantMsg: ChatMessage = {
        ...data.message,
        movies: data.movies || [],
      };
      setMessages((prev) => [...prev, assistantMsg]);
    } catch (err: any) {
      const errorMsg: ChatMessage = {
        id: Date.now() + 1,
        role: "assistant",
        content: err.response?.data?.detail || "Something went wrong. Please try again.",
        movie_ids: [],
        movies: [],
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const hasMovies = messages.some(msg => msg.movies?.length > 0);

  const [muted, setMuted] = useState(true);

  const lastMoviesIndex = messages.reduce(
    (acc, msg, idx) => (msg.movies && msg.movies.length > 0 ? idx : acc),
    -1
  );

  const lastUserMessageIndex = messages.reduce(
    (acc, msg, idx) => (msg.role === "user" ? idx : acc),
    -1
  );

  // True while the movie carousel is the primary content on screen.
  // Goes false as soon as loading starts (user sent a new message).
  const moviesAreVisible = lastMoviesIndex !== -1 && lastMoviesIndex > lastUserMessageIndex && !loading;



  // const newChat = () => {
  //   setMessages([]);
  //   setConversationId(null);
  // };

  return (
    <div className={styles.page}>
      <Header source={ROUTES.CHAT_TEST} clickHandler={() => {}} className={styles.headerWrapperFilters}/>

      <GradientBackdrop/>

      <div className={`${styles.chatArea}`}>
        <div className={`${styles.chatSection} ${
          (hasMovies || loading) ? styles.chatSectionWithMovies : ""
        }`}>

          {messages.length === 0 && (
            <div className={`${styles.chatWrapper} ${styles.fadeIn}`}>
              <div className={styles.welcome}>
                <h2>{t("chat_welcome_title", "Hello, Viewer!")}</h2>
                <p>{t("chat_welcome_subtitle", "I'm your personal guide")}</p>
              </div>
            </div>
          )}

          {loading && (
            <div className={`${styles.chatWrapper} ${styles.fadeIn}`}>
              <div className={styles.chatHeading}>
                <AiIcon/>
                <span> {t("chat_searching", "Searching...")}</span>
              </div>
            </div>
          )}

          {messages.map((msg, index) => {
            const hasMovies = msg.movies && msg.movies.length > 0;
            const isLastMovies = hasMovies && index === lastMoviesIndex && !loading && lastMoviesIndex > lastUserMessageIndex;

            return (
              <div
                key={msg.id}
                className={`${styles.message} ${
                  msg.role === "user" ? styles.userMessage : styles.assistantMessage
                }`}
              >
                {msg.content && !moviesAreVisible && (
                  <div className={`${styles.chatWrapper} ${styles.fadeIn}`}>
                    <div className={styles.messageWrapper}>
                      <div className={styles.messageContent}>
                        {msg.content}
                      </div>
                    </div>
                  </div>
                )}

                {isLastMovies && (
                  <div className={`${styles.moviesList} ${styles.fadeIn}`}>
                    <div className={`${styles.chatWrapper} ${styles.fadeIn}`}>
                      <div className={styles.chatHeading}>
                        <AiIcon/>
                        <span> {t("chat_found", "Here's what I found...")}</span>
                      </div>
                    </div>

                    {isMobile ? (
                      <MovieCarouselMobile
                        movies={msg.movies}
                        likeHandler={likeHandler}
                        favouritesHandler={favouritesHandler}
                        likesList={likesList?.data?.results || []}
                        favoritesList={favoritesList?.data?.results || []}
                        updateVoteLocal={updateVoteCount}
                        isMobile
                        openLoginModal={openLoginModal}
                        muted={muted}
                        onToggleMuted={() => setMuted((prev) => !prev)}
                        onShare={(id) => {
                          setShareMovieId(id);
                          dispatch(setShareModal(true));
                        }}
                      />
                    ) : (
                      <MovieCarouselDesktop
                        movies={msg.movies}
                        likeHandler={likeHandler}
                        favouritesHandler={favouritesHandler}
                        likesList={likesList?.data?.results || []}
                        favoritesList={favoritesList?.data?.results || []}
                        updateVoteLocal={updateVoteCount}
                        isMobile
                        openLoginModal={openLoginModal}
                        muted={muted}
                        onToggleMuted={() => setMuted((prev) => !prev)}
                        onShare={(id) => {
                          setShareMovieId(id);
                          dispatch(setShareModal(true));
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {loading && (
            <div className={`${styles.message} ${styles.assistantMessage}`}>
              <div className={`${styles.chatWrapper} ${styles.fadeIn}`}>
                <div className={styles.messageWrapper}>
                  <div className={styles.messageContent}>
                    <div className={styles.assistantTyping}>
                      <span /><span /><span />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div
            className={`${styles.chatWrapper} ${styles.fadeIn} ${
              (hasMovies || loading) ? styles.chatWrapperWithMovies : ""
            }`}
          >
            <div className={styles.inputWrapper}>
              <textarea
                className={styles.input}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t("chat_placeholder", "What would you like to watch?")}
                disabled={loading}
                maxLength={2000}
              />
              <button
                className={styles.sendBtn}
                onClick={sendMessage}
                disabled={!input.trim() || loading}
              >
                <ArrowUp/>
              </button>
              <span className={styles.textWrap}>Ai</span>
            </div>
          </div>

        </div>
      </div>

      {loginModal && <LoginModal />}

      {shareModal && shareMovieId && (
        <ShareModal
          currentId={shareMovieId}
          shareHandler={() => {}}
          shared={false}
          updateFieldLocal={() => {}}
        />
      )}

      <Footer source={""} />
    </div>
  );
};

export default ChatTestPage;
